<template>
  <div ref="normal" class="zm-sticky">
    <slot></slot>
    <div :class="appendCls" class="zm-sticky-fixed" ref="sticky">
    </div>
  </div>
</template>

<script>
  import AriaUtils from '../../utils/aria-utils';
  import debounce from 'throttle-debounce/debounce';
  import { raf } from '../../utils/scroll';

  const EVENT_CHANGE = 'sticky-change';

  export default {
    name: 'ZmSticky',
    props: {
      checkPosition: {
        type: String,
        default: 'bottom'
      },
      enabled: Boolean,
      offset: {
        type: Number,
        default: 0
      },
      bottomOffset: {
        type: Number,
        default: 0
      },
      stickyHeight: {
        type: Number,
        default: 0
      },
      zIndex: {
        type: Number,
        default: 0
      },
      maxTop: {
        type: Number,
        default: 0
      },
      flow: Boolean,
      appendCls: String,
      toggleClonedVisible: Boolean,
      keepFocus: Boolean,
      debounce: {
        type: Number,
        default: 0
      }
    },
    data() {
      return {
        disabled: !this.enabled,
        isSticky: false
      };
    },
    created() {
      this.ticking = false;
    },
    mounted() {
      this.makeCloneNode();
      this.debounceScroll = this.debounce === 0 ? this.onScroll : debounce(this.debounce, this.onScroll);
      // check and set new position on resize
      const scrollBar = this.findScrollbar();
      if (scrollBar) {
        scrollBar.$on('scroll', this.debounceScroll);
      } else {
        window.addEventListener('scroll', this.debounceScroll, { passive: true });
      }
      window.addEventListener('resize', this.onResize, { passive: true });
      this.triggerScroll();
    },
    methods: {
      makeCloneNode() {
        this.cloneNode = this.$slots.default[0].elm.cloneNode(true);
        this.cloneNode.style.display = '';
        this.cloneNode.style.visibility = 'hidden';
        this.cloneNode.style.position = 'absolute';
        this.cloneNode.style.top = 0;
        this.cloneNode.style.transform = 'none';
        // this.cloneNode.style.width = '100%';
        this.$refs.normal.appendChild(this.cloneNode);
      },
      updateCloneNode() {
        if (this.cloneNode) {
          this.$refs.normal.removeChild(this.cloneNode);
          this.makeCloneNode();
          this.triggerScroll(true);
        }
      },
      makeSticky(targetEL, stickyEL, checkPosition, rect) {
        this.isSticky = true;
        stickyEL.appendChild(targetEL);
        if (checkPosition === 'top') {
          stickyEL.style.top = `${this.offset}px`;
        } else {
          stickyEL.style.bottom = `${this.offset}px`;
        }
        stickyEL.style.left = `${rect.left}px`;
        stickyEL.style.width = `${rect.width}px`;
        if (this.stickyHeight) {
          stickyEL.style.height = `${this.stickyHeight}px`;
        }
        if (this.zIndex) {
          stickyEL.style.zIndex = `${this.zIndex}`;
        }
        document.body.appendChild(stickyEL);
      },
      makeNormal(normalEL, targetEL, stickyEL) {
        if (this.isSticky) {
          this.isSticky = false;
          normalEL && normalEL.insertBefore(targetEL, this.cloneNode);
          if (stickyEL) {
            document.body.removeChild(stickyEL);
          }
        }
      },
      onResize() {
        this.onScroll(null, true);
      },
      onScroll(e, forceUpdate) {
        if (this.disabled) {
          return;
        }
        if (!this.ticking) {
          this.ticking = true;
          raf(() => {
            this.update(e, forceUpdate);
          });
        }
      },
      update(e, forceUpdate) {
        this.ticking = false;
        const normalEL = this.$refs.normal;
        const stickyEL = this.$refs.sticky;
        if (this.keepFocus) {
          this.cacheFocusElement(normalEL, stickyEL);
        }
        const targetEL = this.$slots.default[0].elm;
        const winHeight = window.innerHeight;
        const targetHeight = targetEL.clientHeight;
        if (!this.flow && normalEL) {
          normalEL.style.height = `${targetHeight}px`;
        }
        const rect = this.cloneNode.getBoundingClientRect();
        const checkPosition = this.checkPosition;
        const offset = this.offset;
        const maxTop = this.maxTop;
        const isSticky = this.isSticky;
        const bottomOffset = this.bottomOffset;
        if (checkPosition === 'bottom') {
          if (rect.top <= winHeight - offset - rect.height) {
            this.makeNormal(normalEL, targetEL, stickyEL);
          } else if (stickyEL && (!isSticky || forceUpdate)) {
            this.makeSticky(targetEL, stickyEL, checkPosition, rect);
          }
        } else if (checkPosition === 'top') {
          const scrollTop = (e && e.scrollTop) || document.documentElement.scrollTop || document.body.scrollTop;
          const scrollHeight = (e && e.scrollHeight) || document.documentElement.scrollHeight;
          const offsetToBottom = scrollHeight - scrollTop - targetHeight - offset;
          if (rect.top > offset || (maxTop && (rect.top - offset) < -maxTop) || offsetToBottom <= bottomOffset) {
            this.makeNormal(normalEL, targetEL, stickyEL);
          } else if (stickyEL && (!isSticky || forceUpdate)) {
            this.makeSticky(targetEL, stickyEL, checkPosition, rect);
          }
        }
      },
      disable() {
        this.disabled = true;
      },
      enable() {
        this.disabled = false;
        this.triggerScroll();
      },
      triggerScroll(forceUpdate) {
        this.$nextTick(() => {
          this.onScroll(null, forceUpdate);
        });
      },
      findFirstFocusElement(element) {
        if (AriaUtils.isFocusable(element)) {
          return element;
        }
        for (let i = 0; i < element.childNodes.length; i++) {
          const child = element.childNodes[i];
          const el = this.findFirstFocusElement(child);
          if (el) {
            return el;
          }
        }
        /* istanbul ignore next */
        return null;
      },
      cacheFocusElement(normalEL, stickyEL) {
        const activeElement = document.activeElement;
        if (normalEL.contains(activeElement) || stickyEL.contains(activeElement)) {
          this.focusElement = activeElement;
        } else {
          this.focusElement = null;
        }
      },
      findScrollbar() {
        let parent = this.$parent || this.$root;
        let name = parent.$options.name;

        while (parent && (name !== 'ZmScrollbar')) {
          parent = parent.$parent;

          if (parent) {
            name = parent.$options.name;
          }
        }
        return parent;
      }
    },
    beforeDestroy() {
      const scrollBar = this.findScrollbar();
      if (scrollBar) {
        scrollBar.$off('scroll', this.debounceScroll);
      } else {
        window.removeEventListener('scroll', this.debounceScroll);
      }
      window.removeEventListener('resize', this.onResize);

      const stickyEL = this.$refs.sticky;
      const parentTagName = stickyEL.parentNode && stickyEL.parentNode.tagName.toLowerCase();
      if (stickyEL && document.body.contains(stickyEL) && parentTagName === 'body') {
        document.body.removeChild(stickyEL);
      }
    },
    watch: {
      isSticky: {
        sync: true,
        handler(newVal) {
          let shouldFocus = true;
          if (this.toggleClonedVisible) {
            if (newVal) {
              this.cloneNode.style.visibility = '';
            } else {
              if (this.findFirstFocusElement(this.cloneNode) === document.activeElement) {
                const el = this.findFirstFocusElement(this.$slots.default[0].elm);
                if (el) {
                  this.$nextTick(() => {
                    el.focus();
                  });
                  shouldFocus = false;
                }
              }
              this.cloneNode.style.visibility = 'hidden';
            }
          }
          if (this.focusElement && shouldFocus) {
            this.$nextTick(() => {
              this.focusElement.focus({
                preventScroll: true
              });
            });
          }
          this.$emit(EVENT_CHANGE, newVal);
        }
      },
      offset() {
        this.triggerScroll(true);
      }
    }
  };
</script>

<style lang="scss">
  .zm-sticky {
    position: relative;
  }

  .zm-sticky-fixed {
    position: fixed !important;
    z-index: 10;
  }
</style>
